import { useMutation } from '@apollo/client/react';
import React, { useCallback } from 'react';
import { DELETE_CUSTOMER as DELETE_USER } from '../user.graphql';
import { Dialog, DialogTitle, DialogContent, Typography, DialogActions, Button } from '@mui/material';

interface DeleteUsersProps {
  open: boolean;
  onClose: () => void;
  userId: number[];
  setSelected(val: string[]): void;
  users: { id: number; displayName: string }[];
  removeFromList: (id: number) => void;
}

const brukerWord = (count: number) => (count === 1 ? 'bruker' : 'brukere');

function DeleteUsers({ open, onClose, userId: userIdList, setSelected, users, removeFromList }: DeleteUsersProps) {
  const brukere = brukerWord(userIdList.length);
  const askMessage = userIdList.length > 0 ? `Vil du slette og anonymisere ` + userIdList.length + ` ` + brukere + `?` : `Ingen brukere valgt!`;

  const [message, setMessage] = React.useState<string>(askMessage);
  const [disableOk, setDisableOk] = React.useState<boolean>(false);
  const [denyText, setDenyText] = React.useState<string>('Lukk');
  const [completed, setCompleted] = React.useState<boolean>(false);

  const mutationInput = (parameters) => {
    return { variables: { input: parameters } };
  };

  const continueDelete = React.useRef(false);
  const numberDeleted = React.useRef(0);
  const errors = React.useRef('');

  const [deleteUser] = useMutation<any, any>(DELETE_USER, {
    onCompleted: () => { },
    onError: (error) => {
      setMessage('Feil ved sletting av bruker: ' + error.message);
    },
  });

  const handleDelete = useCallback(async () => {
    continueDelete.current = true;
    setDenyText('Avbryt');
    setDisableOk(true);

    for (const userId of userIdList) {
      if (!continueDelete.current) {
        const cancelMessage = 'Sletting avbrutt ' + (errors.current === '' ? 'uten feil' : 'med feil på ' + brukerWord(errors.current.split(',').filter(Boolean).length) + ' med id: ' + errors.current);
        setMessage(cancelMessage);
        setDisableOk(false);
        setSelected([]);
        return;
      }
      numberDeleted.current++;
      var userIndeletion = users.find((x) => x.id === userId);
      setMessage(
        'Sletter bruker: ' + userIndeletion?.displayName + ' (' + numberDeleted.current + ' av ' + userIdList.length + ')',
      );
      await deleteUser(mutationInput({ id: userId }));
      removeFromList(userId);
    }
    const finishMessage = 'Sletting ferdig ' + (errors.current === '' ? 'uten feil' : 'med feil på ' + brukerWord(errors.current.split(',').filter(Boolean).length) + ' med id: ' + errors.current);
    setMessage(finishMessage);
    setDisableOk(false);
    setDenyText('Lukk');
    setCompleted(true);
    continueDelete.current = false;
  }, [deleteUser, removeFromList, setSelected, userIdList, users]);

  const stopDeletionProcess = useCallback(() => {
    setDenyText('Lukk');

    if (!continueDelete.current) {
      onClose();
      return;
    }
    continueDelete.current = false;
  }, [onClose]);

  return (
    <Dialog open={open} onClose={stopDeletionProcess}>
      <DialogTitle>Slett {brukere}</DialogTitle>
      <DialogContent dividers>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions sx={{ mt: 1, mb: 1 }}>
        <Button color="secondary" onClick={stopDeletionProcess}>
          {denyText}
        </Button>
        {!completed && (
          <Button disabled={disableOk} variant="contained" color="error" onClick={handleDelete}>
            Slett
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default DeleteUsers;
